import { useNewJoinersAndAllocationsFormsTemplatesTableController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/NewJoinersAndAllocationsFormsTemplatesTable/useNewJoinersAndAllocationsFormsTemplatesTableController';
import { NewJoinersAndAllocationsFormTemplate } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/useNewJoinersAndAllocationsKYCController';
import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { DeleteButtonSquare } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { HeaderCell, Cell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import {
	BodyRow,
	HeaderRow,
	DefaultTable,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DataCustomDetailTct } from '@dealstack/shared/lib/generated';
import { get } from 'lodash';
import { memo, useState } from 'react';
import styled from 'styled-components';

import { NewJoinersAndAllocationsFormTemplateInstructionModal } from './NewJoinersAndAllocationsFormTemplateInstructionModal';

interface Props {
	className?: string;
	type: DataCustomDetailTct['type'];
}

const NewJoinersAndAllocationsFormsTemplatesTable = ({ type, className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const {
		formsTemplates,
		mainForm,
		mainFormFieldName,
		onRemoveRowClicked,
		uploadFileRequest,
		showPreviewDocument,
	} = useNewJoinersAndAllocationsFormsTemplatesTableController(type);

	const [openedInstructionFieldName, setOpenedInstructionFieldName] = useState<string>();

	return (
		<div className={className}>
			{!!formsTemplates?.length && (
				<DefaultTable maxSize>
					<HeaderRow>
						<HeaderCell size={0.5}>#</HeaderCell>
						<HeaderCell customSize="1fr">
							{tMipCreate('contracts.table.headers.filename')}
						</HeaderCell>
						<HeaderCell
							customSize="1fr"
							childrenTooltip={tMipCreate('contracts.table.tooltips.contract_name')}
						>
							{tMipCreate('contracts.table.headers.form_name')}
						</HeaderCell>
						<HeaderCell
							customSize="1fr"
							childrenTooltip={tMipCreate(
								'contracts.table.tooltips.instruction_to_investors'
							)}
						>
							{tMipCreate('contracts.table.headers.instructions')}
						</HeaderCell>
						<HeaderCell size={2} />
					</HeaderRow>
					<BodyRow>
						{formsTemplates?.map(
							(formTemplate: NewJoinersAndAllocationsFormTemplate, index) => {
								const formTemplateFieldName = `${mainFormFieldName}.${index}`;

								const { errors } = mainForm.formState;
								const isInvalid = !!get(
									errors,
									`${mainFormFieldName}.${index}.name`
								);

								return (
									<RowWrapper key={formTemplate.id} isInvalid={isInvalid}>
										<Row>
											<Cell>
												<Text bold variant="p-sm">
													{index + 1}.
												</Text>
											</Cell>
											<Cell>
												<ShowPreviewWrapper
													onClick={() => {
														void showPreviewDocument(formTemplate.id);
													}}
												>
													<Text bold variant="p-sm" elipsis>
														{formTemplate.fileName}
													</Text>
												</ShowPreviewWrapper>
											</Cell>
											<Cell>
												<StringInput
													name={`${formTemplateFieldName}.name`}
													disabled={!formTemplate.local}
												/>
											</Cell>
											<Cell>
												{formTemplate.instruction?.length ? (
													<EditWrapper>
														<ChipsV2
															prefixIcon="checkmark-circle-contained-filled"
															size="s"
															color={COLOR.GREEN}
															chipTheme={CHIP_THEME.LIGHT}
														/>
														<Button
															onClick={() =>
																setOpenedInstructionFieldName(
																	`${formTemplateFieldName}.instruction`
																)
															}
															iconSize="lg"
															icon="edit-pencil"
															iconPlacement="left"
															size="medium"
															variant="text"
															disabled={!formTemplate.local}
														>
															{tMipCreate('contracts.table.edit')}
														</Button>
													</EditWrapper>
												) : (
													<Button
														onClick={() =>
															setOpenedInstructionFieldName(
																`${formTemplateFieldName}.instruction`
															)
														}
														iconSize="lg"
														icon="add-circle"
														iconPlacement="left"
														size="medium"
														variant="text"
														disabled={!formTemplate.local}
													>
														{tMipCreate('contracts.table.add')}
													</Button>
												)}
											</Cell>
											<Cell>
												{formTemplate.local && (
													<DeleteButtonSquare
														onClick={() => {
															onRemoveRowClicked(formTemplate.id);
														}}
													/>
												)}
											</Cell>
										</Row>
									</RowWrapper>
								);
							}
						)}
					</BodyRow>
				</DefaultTable>
			)}

			<DragAndDrop
				uploadFileRequest={uploadFileRequest}
				accept={{ 'application/pdf': ['.pdf'] }}
				invalidTypeMessage={tMipCreate('contracts.table.errors.invalid_file_type')}
			/>

			{openedInstructionFieldName && (
				<NewJoinersAndAllocationsFormTemplateInstructionModal
					onClose={() => setOpenedInstructionFieldName(undefined)}
					fieldName={openedInstructionFieldName}
				/>
			)}
		</div>
	);
};

const ShowPreviewWrapper = styled.div`
	width: 100%;
	cursor: pointer;
`;

const EditWrapper = styled.div`
	display: flex;
	flex-direction: row;
	padding: 0 0.3em;
	justify-content: center;
	align-items: center;
`;

const Memoized = memo(NewJoinersAndAllocationsFormsTemplatesTable);
const Styled = styled(Memoized)`
	${DefaultTable} {
		margin-bottom: 1em;
	}
`;

export { Styled as NewJoinersAndAllocationsFormsTemplatesTable };
